import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';
import { Loader2 } from 'lucide-react';

interface ReportFormat {
  id: string;
  name: string;
  settings: {
    companyLogo: boolean;
    companyName: boolean;
    reportType: boolean;
    preparedBy: boolean;
    generatedOn: boolean;
    projectSnapshot: boolean;
    projectHighlights: boolean;
    tasksSnapshot: boolean;
    tasksHighlights: boolean;
    teamProductivity: boolean;
    additionalInfo: boolean;
    meetingMinutes: boolean;
  };
}

interface ReportFormatSelectorProps {
  onFormatSelect: (format: ReportFormat | null) => void;
}

export default function ReportFormatSelector({ onFormatSelect }: ReportFormatSelectorProps) {
  const [loading, setLoading] = useState(true);
  const [formats, setFormats] = useState<ReportFormat[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<string>('default');

  // Default format
  const defaultFormat: ReportFormat = {
    id: 'default',
    name: 'Default Format',
    settings: {
      companyLogo: true,
      companyName: true,
      reportType: true,
      preparedBy: true,
      generatedOn: true,
      projectSnapshot: true,
      projectHighlights: true,
      tasksSnapshot: true,
      tasksHighlights: true,
      teamProductivity: true,
      additionalInfo: true,
      meetingMinutes: false
    }
  };

  useEffect(() => {
    fetchFormats();
  }, []);

  const fetchFormats = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('custom_report_formats')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFormats(data || []);
      
      // If there are formats, select the first one by default
      if (data && data.length > 0) {
        setSelectedFormat(data[0].id);
        onFormatSelect(data[0]);
      }
    } catch (error) {
      console.error('Error fetching formats:', error);
      toast.error('Failed to load report formats');
      setSelectedFormat('default');
      onFormatSelect(defaultFormat);
    } finally {
      setLoading(false);
    }
  };

  const handleFormatChange = (formatId: string) => {
    setSelectedFormat(formatId);
    if (formatId === 'default') {
      onFormatSelect(defaultFormat);
    } else {
      const selectedCustomFormat = formats.find(f => f.id === formatId);
      onFormatSelect(selectedCustomFormat || null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 p-2 mb-2">
        <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading formats...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <select
          id="format"
          value={selectedFormat}
          onChange={(e) => handleFormatChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="default">Default Format</option>
          {formats?.map((format) => (
            <option key={format.id} value={format.id}>
              {format.name}
            </option>
          ))}
        </select>
      </div>

      {selectedFormat !== 'default' && formats?.length > 0 && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Using custom format: {formats?.find(f => f.id === selectedFormat)?.name}
        </div>
      )}
    </div>
  );
}